import { CallContactsSummary } from 'components/contacts/summaries/call_contacts_summary'
import { useCallManager } from 'contexts/call_manager_context'
import { useParticipantManager } from 'contexts/participant_manager_context'
import { useMediaQuery } from 'hooks/use_media_query'
import { CallCycleCameraIcon } from 'images'
import React, { useMemo, useRef } from 'react'

import { MEDIA_QUERIES, css } from 'design'
import { Card } from 'primitives/card'
import { Box } from 'primitives/containers'

import { isLiveChatApp } from '../../reduxes/slices/live_chat_app/selectors'
import { useSelectorDelegateStore } from '../../reduxes/stores/root_store/hooks'
import { TrackControls } from './controls/track_controls'
import { useCycleCameras } from './controls/use_cycle_cameras'
import { Gallery } from './views/gallery'

export default function Room() {
  const containerRef = useRef(null)
  const liveChatApp = useSelectorDelegateStore(isLiveChatApp)

  const {
    activeCall: { isOnCall, callRecord, callErrors },
  } = useCallManager()

  const {
    localParticipant,
    localTrackData,
    remoteParticipantsWithVideo,
    isVideoActive,
  } = useParticipantManager()
  const {
    isVideoEnabled,
    videoDevices,
    videoDeviceId,
    handleVideoDeviceChange,
  } = localTrackData
  const desktop = useMediaQuery(MEDIA_QUERIES.md)

  const videoDeviceIds = useMemo(() => {
    return Object.values(videoDevices || {}).map((device) => device.deviceId)
  }, [videoDevices])

  const {
    toggled: cycleCameraButtonToggled,
    onClick: cycleCameraButtonOnClick,
  } = useCycleCameras({
    videoDeviceId,
    videoDeviceIds,
    handleVideoDeviceChange,
  })

  return (
    <Box
      as="section"
      css={{
        width: '100%',
        flexGrow: 1,
        position: 'relative',
        overflow: 'hidden',
        background: '$colors$grey50',
      }}
      className="topic__calls__root"
      ref={containerRef}
    >
      <section className={participantContainerStyles()}>
        {isVideoActive && (
          <Gallery
            remoteParticipants={remoteParticipantsWithVideo || []}
            localParticipant={localParticipant}
            localTrackData={localTrackData}
            callRecord={callRecord}
          />
        )}

        {(!desktop || liveChatApp) && (
          <CallContactsSummary isVideoActive={isVideoActive} />
        )}
      </section>

      {/*
        At mobile breakpoints, we hide the TrackControls in the header as they're not relevant here
        The user has a specific Call view that contains the track controls, and the flip camera button
        if it is available to them
      */}
      {(!desktop || liveChatApp) && (
        <>
          {videoDeviceIds.length > 1 && isVideoEnabled && (
            <Box
              as="section"
              css={{
                position: 'absolute',
                right: '$space$5',
                top: '$space$5',
              }}
            >
              <button
                className={cycleCameraButtonStyles({
                  toggled: cycleCameraButtonToggled,
                })}
                onClick={cycleCameraButtonOnClick}
                aria-label="Cycle Camera"
              >
                <CallCycleCameraIcon />
              </button>
            </Box>
          )}
          <Box
            as="section"
            css={{
              $$insetSize: '$space$5',
              position: 'absolute',
              left: '$$insetSize',
              right: '$$insetSize',
              bottom: '$$insetSize',
            }}
          >
            {isOnCall && <TrackControls className={trackControlsStyles()} />}
          </Box>
        </>
      )}

      <Box
        as="section"
        css={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          bottom: '$space$9',
          pointerEvents: 'none',

          '@md': {
            bottom: '$space$5',
            left: '$space$5',
            transform: 'initial',
          },
        }}
        className="topic__calls__root__status"
      >
        {callErrors?.length ? (
          <Card className="topic__calls__statusmessage" boxShadow="0">
            {callErrors.map((error) => (
              <h2 key={error}>{error}</h2>
            ))}
          </Card>
        ) : null}
      </Box>
    </Box>
  )
}

const trackControlsStyles = css({
  justifyContent: 'center',
})

const participantContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
})

const cycleCameraButtonStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'hsla(0, 0%, 0%, 0.6)',
  borderRadius: '50%',
  border: 'none',
  width: 42,
  height: 42,
  fill: 'white',
})
